import { FC } from 'react';
import { Alert, DropdownMenu, Icon, IconButton } from '@kontentino/ui';
import {
  STATUS,
  TYPE as POST_TYPE,
} from '@kontentino/kontentino-constants/Posts';
import { faCheck } from '@fortawesome/pro-regular-svg-icons/faCheck';
import { faEllipsisV } from '@fortawesome/pro-regular-svg-icons/faEllipsisV';
import { faRotateRight } from '@fortawesome/pro-regular-svg-icons/faRotateRight';
import { faXmark } from '@fortawesome/pro-regular-svg-icons/faXmark';
import PostModalFooterSendForApprovalDropdown from 'app/modules/posts/modules/detail/components/postModalFooter/PostModalFooterSendForApprovalDropdown';
import PermissionsGateButton from 'app/components/PermissionsGateButton';
import { UserPermission } from 'constants/userPermission';
import AssignTaskWorkflowButton from 'app/modules/posts/modules/detail/components/postModalFooter/AssignTaskWorkflowButton';
import ScheduleWorkflowButton from 'app/modules/posts/modules/detail/components/postModalFooter/ScheduleWorkflowButton';
import { PostDetail, SchedulingInformation } from 'types/PostDetail';
import UserPermissionGate from 'components/UserPermissionGate';
import PostScheduledInformation from './postModalFooter/PostScheduledInformation';
import { getPostFooterInfoMessage } from 'app/modules/posts/modules/detail/utils/getPostFooterInfoMessage';
import Popup from 'utils/popup';
import { Trans, useTranslation } from 'react-i18next';
import { PostScheduleValidation } from 'api/post';
import { getValidationMessagesCount } from '../utils/getValidationMessagesCount';
import { CommentThread } from 'app/modules/comments/types';
import Link from 'components/shared/Link';
import ReactDOMServer from 'react-dom/server';
import GlobalPostScheduleWorkflowButton from './postModalFooter/GlobalPostScheduleWorkflowButton';
import { ScheduleMethodType } from 'app/modules/posts/types/ScheduleMethodType';
import DomUtils from 'app/utils/dom';
import { useWidgetsSetupApi } from 'components/posts/detail/hooks/useWidgetsSetupApi';

type Props = {
  post: PostDetail;
  schedulingInfo: {
    isLoading: boolean;
    canSchedule: boolean;
    canExport: boolean;
    info: SchedulingInformation | null;
    canPublishNow: boolean;
    canCreateDarkPost: boolean;
  };
  workflowEventHandlers: {
    onChangeToDraft: () => void;
    onAssignTask: () => void;
    onSchedule: (type?: ScheduleMethodType) => void;
    onMarkAsDone: () => void;
    onSendForInternalApproval: () => void;
    onSendForClientApproval: () => void;
    onSharePublicLink: () => void;
    onRespondToApprovalRequest: (
      type: 'approve' | 'rework',
      thread: CommentThread,
    ) => void;
  };
  scheduleValidationMessages: PostScheduleValidation['messages'] | undefined;
};

const PostDetailModalFooter: FC<Props> = ({
  post,
  schedulingInfo,
  workflowEventHandlers,
  scheduleValidationMessages,
}) => {
  const { t } = useTranslation();
  const assignTaskButtonProps = { onClick: workflowEventHandlers.onAssignTask };
  const sendForApprovalDropdownProps = {
    onClientApprovalClick: workflowEventHandlers.onSendForClientApproval,
    onInternalApprovalClick: workflowEventHandlers.onSendForInternalApproval,
    onSharePublicLinkClick: workflowEventHandlers.onSharePublicLink,
  };
  const { query, mutation } = useWidgetsSetupApi({
    target: 'post-preview',
  });

  const PostRecommendationTranslatedText = (
    <Trans
      i18nKey="validation.postRecommendations.text"
      components={[
        <Link
          to="https://intercom.help/kontentino/en/articles/7995045-media-specifications"
          target="_blank"
          className="tw-tex-underline tw-font-medium tw-text-primary-100 tw-underline hover:tw-no-underline"
        />,
      ]}
    />
  );

  function canSkipConfirmationPopup() {
    return (
      !getValidationMessagesCount(scheduleValidationMessages) ||
      post.type === POST_TYPE.COMMENT
    );
  }

  function scrollToPostCompletion() {
    const postContentRight = document.querySelector<HTMLDivElement>(
      '[id="post-preview_content-right"]',
    );

    const postCompletionWidget = document.querySelector<HTMLDivElement>(
      `[id="posts-preview_completion"]`,
    );

    if (postCompletionWidget && postContentRight) {
      DomUtils.scrollParentToChild(postContentRight, postCompletionWidget, 100);

      if (query.data) {
        mutation.mutate(
          query.data.map((widget) =>
            widget.key === 'postCompletion'
              ? { ...widget, isExpanded: true }
              : widget,
          ),
        );
      }
    }
  }

  const scheduleButtonProps = {
    onSchedule: async (type?: ScheduleMethodType) => {
      if (!type && !schedulingInfo.canSchedule) {
        scrollToPostCompletion();
        return;
      }

      if (canSkipConfirmationPopup()) {
        workflowEventHandlers.onSchedule(type);
        return;
      }

      const { isConfirmed } = await Popup.confirm({
        title: t('validation.postRecommendations.title'),
        html: ReactDOMServer.renderToString(PostRecommendationTranslatedText),
        customClass: {
          confirmButton: 'tw-flex-1',
          cancelButton: 'tw-flex-1',
          actions:
            'tw-flex tw-gap-4 tw-border-t tw-border-t-grayscale-20 !tw-p-6',
          popup: '!tw-p-0 md:!tw-w-[480px]',
          htmlContainer: '!tw-px-6 !tw-text-grayscale-140',
          title: '!tw-px-6 !tw-pt-6',
        },
      });

      if (isConfirmed) {
        workflowEventHandlers.onSchedule(type);
      }
    },
    post,
    isLoading: schedulingInfo.isLoading,
    isDisabled: false,
    schedulingOptions: [
      {
        option: 'publishNow' as const,
        disabled: !schedulingInfo.canPublishNow,
      },
      {
        option: 'createDarkPost' as const,
        disabled: !schedulingInfo.canCreateDarkPost,
      },
      {
        option: 'exportInAdvance' as const,
        disabled: !schedulingInfo.canExport,
      },
    ],
    scheduleButtonTooltipContent: !schedulingInfo.canSchedule
      ? t('scheduleCompletionTooltipMessage')
      : undefined,
  };

  const renderPostReadyCTAs = () => {
    return (
      <>
        <AssignTaskWorkflowButton {...assignTaskButtonProps} />
        <PostModalFooterSendForApprovalDropdown
          {...sendForApprovalDropdownProps}
        />
        {scheduleButtonProps.post.localPosts ? (
          <GlobalPostScheduleWorkflowButton post={post} />
        ) : (
          <ScheduleWorkflowButton {...scheduleButtonProps} />
        )}
      </>
    );
  };

  const renderPostInProgressCTAs = () => {
    return (
      <>
        <AssignTaskWorkflowButton
          {...assignTaskButtonProps}
          reassign={post.status === STATUS.WAITING_FOR}
        />
        {post.status === STATUS.WAITING_FOR ? (
          <PermissionsGateButton
            data-cy="post-mark-as-done-button"
            scopes={UserPermission.POST_MARK_AS_DONE}
            onClick={workflowEventHandlers.onMarkAsDone}
            iconBefore={<Icon icon={faCheck} />}
          >
            {t('markAsDone')}
          </PermissionsGateButton>
        ) : (
          <PostModalFooterSendForApprovalDropdown
            {...sendForApprovalDropdownProps}
          />
        )}
      </>
    );
  };

  const renderPrivateDraftCTAs = () => {
    return (
      <PermissionsGateButton
        scopes={[UserPermission.POST_EDIT_STATUS]}
        variant="secondary"
        iconBefore={<Icon icon={faRotateRight} />}
        onClick={workflowEventHandlers.onChangeToDraft}
        data-cy="post-change-to-draft-button"
      >
        {t('changeToDraft')}
      </PermissionsGateButton>
    );
  };

  const renderPendingInternalApprovalCTAs = () => {
    return (
      <>
        <PermissionsGateButton
          scopes={[UserPermission.POST_INTERNAL_APPROVE]}
          iconBefore={<Icon icon={faCheck} />}
          onClick={() =>
            workflowEventHandlers.onRespondToApprovalRequest(
              'approve',
              'internal',
            )
          }
          variant="success"
          data-cy="post-internal-approve-button"
        >
          {t('commentsWorkflowActions.internallyApprove')}
        </PermissionsGateButton>
        <PermissionsGateButton
          scopes={[UserPermission.POST_INTERNAL_APPROVE]}
          variant="danger"
          iconBefore={<Icon icon={faXmark} />}
          onClick={() =>
            workflowEventHandlers.onRespondToApprovalRequest(
              'rework',
              'internal',
            )
          }
          data-cy="post-internal-rework-button"
        >
          {t('commentsWorkflowActions.internallyReject')}
        </PermissionsGateButton>
      </>
    );
  };

  const renderPostedCTAs = () => {
    if (!schedulingInfo.info) {
      return null;
    }
    return (
      <PostScheduledInformation
        schedulingInfo={schedulingInfo.info}
        postId={post.id}
        projectId={post.project.id}
        page={post.page}
        publishingMethod={post.attachments.publishingMethod}
      />
    );
  };

  const renderInfoMessage = (message?: string) => {
    return (
      <Alert data-cy="post-information-alert">
        <div className="tw-flex tw-items-center">
          {getPostFooterInfoMessage({
            status: post.status,
            message,
            t,
          })}
          {post.status === STATUS.AT_CLIENT && (
            <UserPermissionGate
              exact={false}
              scopes={[
                UserPermission.POST_CLIENT_APPROVE,
                UserPermission.POST_INTERNAL_APPROVE,
              ]}
            >
              <DropdownMenu.Root>
                <DropdownMenu.Trigger>
                  <IconButton
                    variant="plain"
                    icon={
                      <Icon
                        icon={faEllipsisV}
                        className="tw-cursor-pointer tw-text-grayscale-90"
                        size={'md'}
                        withoutBoundary
                      />
                    }
                  />
                </DropdownMenu.Trigger>
                <DropdownMenu.Content>
                  <UserPermissionGate
                    scopes={UserPermission.POST_CLIENT_APPROVE}
                  >
                    <DropdownMenu.Item
                      onSelect={() =>
                        workflowEventHandlers.onRespondToApprovalRequest(
                          'approve',
                          'client',
                        )
                      }
                    >
                      {t('commentsWorkflowActions.clientApprove')}
                    </DropdownMenu.Item>
                  </UserPermissionGate>
                  <UserPermissionGate
                    scopes={UserPermission.POST_INTERNAL_APPROVE}
                  >
                    <DropdownMenu.Item
                      onSelect={() =>
                        workflowEventHandlers.onRespondToApprovalRequest(
                          'rework',
                          'internal',
                        )
                      }
                    >
                      {t('commentsWorkflowActions.internallyReject')}
                    </DropdownMenu.Item>
                  </UserPermissionGate>
                </DropdownMenu.Content>
              </DropdownMenu.Root>
            </UserPermissionGate>
          )}
        </div>
      </Alert>
    );
  };

  const renderFooterContent = () => {
    switch (post.status) {
      case STATUS.DRAFT:
        return renderPrivateDraftCTAs();
      case STATUS.APPROVED:
      case STATUS.COMPLETED:
      case STATUS.INTERNAL_APPROVED:
      case STATUS.DONE:
      case STATUS.FAILED:
        return renderPostReadyCTAs();
      case STATUS.REJECTED:
      case STATUS.REJECTED_BY_CLIENT:
      case STATUS.WAITING_FOR:
        return renderPostInProgressCTAs();
      // SCHEDULED_EXTERNALLY has no scheduler record behind it, so the scheduling bar
      // would render nothing and leave the footer silent about who publishes the post.
      case STATUS.AT_CLIENT:
      case STATUS.EXPORT_FAILED:
      case STATUS.IS_SCHEDULING:
      case STATUS.SCHEDULED_EXTERNALLY:
        return renderInfoMessage();
      case STATUS.WAITING_FOR_INTERNAL_APPROVAL:
        return renderPendingInternalApprovalCTAs();
      case STATUS.SCHEDULED:
      case STATUS.POSTED:
      case STATUS.EXPORTED:
      case STATUS.POSTED_EXTERNALLY:
        return renderPostedCTAs();
    }
  };

  return (
    <UserPermissionGate
      scopes={[
        UserPermission.POST_INTERNAL_APPROVE,
        UserPermission.POST_CLIENT_APPROVE,
        UserPermission.POST_EDIT_STATUS,
        UserPermission.POST_MARK_AS_DONE,
        UserPermission.USER_ASSIGN_TASK,
        UserPermission.POST_SEE_EXTERNAl,
        UserPermission.POST_COPY_LINK,
      ]}
      exact={false}
    >
      <div
        className="tw-flex tw-items-center tw-justify-between tw-bg-grayscale-30 tw-px-8 tw-py-3"
        data-name="post-preview_workflow-buttons"
      >
        <div className="tw-ml-auto tw-flex tw-items-center tw-gap-4 tw-overflow-y-scroll lg:tw-overflow-y-auto">
          {renderFooterContent()}
        </div>
      </div>
    </UserPermissionGate>
  );
};

export default PostDetailModalFooter;
